// The "Custom plans" section's search-box debounce seam (#4341, #5136). Kept
// separate from `components/custom-plans-keyboard.ts`: that module is named
// and scoped for KEYBOARD handling, and a debounce timer is a different
// concern that does not belong in the same file. This one is named for what
// it does and has no keyboard dependency.

import { createSignal, onCleanup } from 'solid-js';

/** How long to wait between a search keystroke and writing `?q=`. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The search-box debounce seam. The local `qInput` is what the input paints
 * (so a typed letter shows up immediately); the debounced value is what the
 * URL — and so the request — receives. A plain `setTimeout`/`clearTimeout`
 * pair; the timer's "no timer" sentinel is `undefined` (this repo's
 * `toast-seam.test.ts` regex `HAND_ROLLED` rejects a hand-rolled timer that
 * returns `null` for that case). The {@link useDebouncedSearch.debounce}
 * callback receives the trimmed value and is what the section uses to write
 * `?q=` — never `null`.
 *
 * The caller's own effect that mirrors the URL `q` BACK into `qInput` (a
 * back/forward step, a programmatic clear) must compare against the
 * TRIMMED buffer — this seam commits `next.trim()`, so a buffer holding
 * `'audit '` (a trailing space the reader has not finished past yet) is
 * still the same search as a URL holding `'audit'`; treating them as
 * different would reset the box out from under a reader who paused mid-word.
 */
export function useDebouncedSearch(): {
  readonly qInput: () => string;
  readonly setQInput: (next: string) => void;
  readonly debounce: (next: string, commit: (value: string) => void) => void;
} {
  const [qInput, setQInput] = createSignal('');
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
  const debounce = (next: string, commit: (value: string) => void): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => commit(next.trim()), SEARCH_DEBOUNCE_MS);
  };
  return { qInput, setQInput, debounce };
}
